/**
 * Estimated cart tax from shop.metafields.custom.estimated_tax_rates + buyer location.
 */
(function initCartEstimatedTax(global) {
  'use strict';

  let cachedLocation = null;
  let cachedLocationPromise = null;
  let lastEstimate = null;

  const getConfig = () => global.FoxTheme?.estimatedTax;

  const formatMoney = (cents) => {
    if (global.FoxTheme?.Currency?.formatMoney) {
      return global.FoxTheme.Currency.formatMoney(
        cents,
        global.FoxTheme.settings?.moneyFormat || global.Shopify?.money_format
      );
    }
    return (Number(cents) / 100).toFixed(2);
  };

  const normalizeCode = (value) => (value == null ? '' : String(value).trim().toUpperCase());

  const getStoreRoot = () => {
    const root = global.Shopify?.routes?.root;
    if (root == null) return '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getRateEntry = (rates, country, region) => {
    const countryConfig = rates?.countries?.[country];
    if (!countryConfig) return null;

    const regionMap = country === 'CA' ? countryConfig.provinces : countryConfig.states;
    const regionCode = normalizeCode(region);

    if (regionCode && regionMap?.[regionCode]) {
      return { ...regionMap[regionCode], region: regionCode, country };
    }

    if (countryConfig.default) {
      return { ...countryConfig.default, region: regionCode || null, country };
    }

    return null;
  };

  const readGeoCookie = () => {
    try {
      const match = document.cookie.match(/(?:^|;\s*)geo_data=([^;]*)/);
      if (!match) return null;
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (error) {
      return null;
    }
  };

  const getGeoData = () =>
    new Promise((resolve) => {
      if (global.geoData && (global.geoData.countryCode || global.geoData.country?.code)) {
        resolve(global.geoData);
        return;
      }

      const cached = readGeoCookie();
      if (cached && (cached.countryCode || cached.country?.code)) {
        resolve(cached);
        return;
      }

      if (typeof global.Geolizr?.getGeoData === 'function') {
        global.Geolizr.getGeoData((data) => resolve(data || null));
        return;
      }

      resolve(null);
    });

  const resolveLocation = async (config) => {
    const customerCountry = normalizeCode(config.customer?.country);
    const customerProvince = normalizeCode(config.customer?.province);

    if (customerCountry && (customerCountry === 'CA' || customerCountry === 'US')) {
      return {
        country: customerCountry,
        region: customerProvince || null,
        source: 'customer',
      };
    }

    const geo = await getGeoData();
    const geoCountry = normalizeCode(geo?.countryCode || geo?.country?.code);
    const geoRegion = normalizeCode(geo?.region || geo?.regionCode);

    if (geoCountry === 'CA' || geoCountry === 'US') {
      return {
        country: geoCountry,
        region: geoRegion || null,
        source: 'geolizr',
      };
    }

    const localizationCountry = normalizeCode(config.localizationCountry);
    if (localizationCountry === 'CA' || localizationCountry === 'US') {
      return {
        country: localizationCountry,
        region: null,
        source: 'localization',
      };
    }

    return null;
  };

  const resolveLocationCached = async (config) => {
    if (cachedLocation) return cachedLocation;

    if (!cachedLocationPromise) {
      cachedLocationPromise = resolveLocation(config).then((location) => {
        if (location) cachedLocation = location;
        return location;
      });
    }

    return cachedLocationPromise;
  };

  const fetchCartTotalCents = async () => {
    try {
      const response = await fetch(`${getStoreRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return Number(cart.total_price);
    } catch (error) {
      return null;
    }
  };

  const getTaxableTotalCents = (footer) => {
    const marker = footer.querySelector('[data-cart-taxable-total]');
    if (marker) {
      const total = Number(marker.getAttribute('data-cart-taxable-total'));
      if (!Number.isNaN(total) && total >= 0) return total;
    }
    return null;
  };

  const getSubtotalCents = (footer, taxableTotalCents) => {
    let baseCents = Number(taxableTotalCents);
    const subtotalEl = footer.querySelector('[data-estimated-total-subtotal]');
    if (Number.isNaN(baseCents) && subtotalEl) {
      baseCents = Number(subtotalEl.getAttribute('data-cart-subtotal-cents'));
    }
    return Number.isNaN(baseCents) || baseCents < 0 ? null : baseCents;
  };

  const updateSubtotalTotal = (footer, taxableTotalCents) => {
    const subtotalEl = footer.querySelector('[data-estimated-total-subtotal]');
    const baseCents = getSubtotalCents(footer, taxableTotalCents);
    if (!subtotalEl || baseCents == null) return;

    subtotalEl.setAttribute('data-cart-subtotal-cents', String(baseCents));
    subtotalEl.textContent = formatMoney(baseCents);
  };

  const isWithTaxTotalEnabled = (footer) => {
    const cartDrawer = footer.closest('#CartDrawer') || document.getElementById('CartDrawer');
    return Boolean(cartDrawer?.classList.contains('ab-cart-total-with-tax'));
  };

  const showWithTaxTotal = (footer, taxableTotalCents, taxCents) => {
    const withTaxRow = footer.querySelector('[data-estimated-total-with-tax-row]');
    const withTaxEl = footer.querySelector('[data-estimated-total-with-tax]');
    const baseCents = getSubtotalCents(footer, taxableTotalCents);
    if (!withTaxRow || !withTaxEl || baseCents == null || taxCents <= 0) return;

    withTaxEl.textContent = formatMoney(baseCents + taxCents);

    if (!isWithTaxTotalEnabled(footer)) {
      hideWithTaxTotal(footer);
      return;
    }

    withTaxRow.hidden = false;
    withTaxRow.classList.remove('hidden');
  };

  const hideWithTaxTotal = (footer) => {
    const withTaxRow = footer.querySelector('[data-estimated-total-with-tax-row]');
    if (!withTaxRow) return;

    withTaxRow.hidden = true;
    withTaxRow.classList.add('hidden');
  };

  const resetEstimatedTotals = (footer, taxableTotalCents) => {
    updateSubtotalTotal(footer, taxableTotalCents);
    hideWithTaxTotal(footer);
  };

  const updateRow = (row, rateEntry, taxCents) => {
    const labelEl = row.querySelector('.cart-drawer__estimated-tax-label');
    const amountEl = row.querySelector('.cart-drawer__estimated-tax-amount');
    const labelPrefix = global.FoxTheme?.estimatedTax?.labelPrefix || 'Estimated tax';

    if (labelEl) {
      labelEl.textContent = labelPrefix;
    }
    if (amountEl) {
      amountEl.textContent = formatMoney(taxCents);
    }

    const footer = row.closest('[data-estimated-tax-enabled]');
    if (!footer || !isWithTaxTotalEnabled(footer)) {
      hideRow(row);
      return;
    }

    row.hidden = false;
    row.classList.remove('hidden');
  };

  const hideRow = (row) => {
    row.hidden = true;
    row.classList.add('hidden');
  };

  const updateFooter = async (footer) => {
    const config = getConfig();
    const row = footer.querySelector('[data-estimated-tax-row]');
    if (!config?.rates || !row || config.taxesIncluded) {
      if (row) hideRow(row);
      resetEstimatedTotals(footer);
      lastEstimate = null;
      return;
    }

    let taxableTotal = getTaxableTotalCents(footer);
    if (taxableTotal == null || taxableTotal <= 0) {
      taxableTotal = await fetchCartTotalCents();
    }
    updateSubtotalTotal(footer, taxableTotal);

    if (taxableTotal == null || taxableTotal <= 0) {
      hideRow(row);
      hideWithTaxTotal(footer);
      lastEstimate = null;
      return;
    }

    const location = await resolveLocationCached(config);
    let rateEntry = location ? getRateEntry(config.rates, location.country, location.region) : null;

    if (!rateEntry && lastEstimate?.rateEntry) {
      rateEntry = lastEstimate.rateEntry;
    }

    const rate = Number(rateEntry?.rate);
    if (!rateEntry || Number.isNaN(rate) || rate <= 0) {
      hideRow(row);
      hideWithTaxTotal(footer);
      lastEstimate = null;
      return;
    }

    const taxCents = Math.round(taxableTotal * rate);
    if (taxCents <= 0) {
      hideRow(row);
      hideWithTaxTotal(footer);
      lastEstimate = null;
      return;
    }

    lastEstimate = { rateEntry, taxCents, taxableTotal };
    updateRow(row, rateEntry, taxCents);
    showWithTaxTotal(footer, taxableTotal, taxCents);
  };

  const refresh = () => {
    document.querySelectorAll('[data-estimated-tax-enabled]').forEach((footer) => {
      updateFooter(footer);
    });
  };

  const scheduleRefresh = () => {
    refresh();
    global.setTimeout(refresh, 0);
    global.requestAnimationFrame(() => {
      global.requestAnimationFrame(refresh);
    });
    global.setTimeout(refresh, 150);
    global.setTimeout(refresh, 400);
  };

  const boot = () => {
    if (!getConfig()?.rates) return;

    scheduleRefresh();

    const pubsub = global.FoxTheme?.pubsub;
    if (pubsub?.subscribe && pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
    }

    global.document.addEventListener('cart:refresh', scheduleRefresh);
    global.document.addEventListener('subscribfy:updatedExmCartItems', scheduleRefresh);
    global.document.addEventListener('subscribfy:VIPAddedToCart', scheduleRefresh);
    global.document.addEventListener('subscribfy:VIPRemovedFromCart', scheduleRefresh);
    global.addEventListener('pageshow', scheduleRefresh);
  };

  global.CartEstimatedTax = { refresh: scheduleRefresh };

  if (global.document.readyState === 'loading') {
    global.document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
